import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { STORAGE_TEXT, type StorageState, isAnnounced } from './storage.js'

/**
 * De zichtbare opslagstatus (R-80) met de statusregio die R-141 vraagt.
 *
 * Twee lagen, en dat is het hele punt: wat de invuller ziet verandert vaker dan wat
 * een schermlezer te horen krijgt. De zichtbare tekst volgt elke stand; de
 * statusregio krijgt alleen "bewaard" en "mislukt", want "opslaan…" komt bij elke
 * aanslag terug en zou een schermlezer onbruikbaar maken.
 *
 * `role="status"` en niet `role="alert"`: de melding verplaatst de focus niet en
 * onderbreekt niet wat de invuller op dat moment doet.
 */
export interface StorageStatusProps {
  readonly stand: StorageState
  readonly taal: Language
  /**
   * Het tijdstip van de laatste geslaagde opslag, al in de kloktijd van de lezer ("14:32"). Dan zegt
   * de rust- en de bewaarstand "Opgeslagen om 14:32", zoals het ontwerp van het invulscherm: één plek
   * voor het opslagmoment, en niet ook nog een regel in de voet (audit R-43).
   */
  readonly opgeslagenOm?: string
}

export function StorageStatus({ stand: state, taal: language, opgeslagenOm: savedAt }: StorageStatusProps): ReactNode {
  const text =
    savedAt !== undefined && (state === 'rustig' || state === 'bewaard')
      ? language === 'nl'
        ? `Opgeslagen om ${savedAt}`
        : `Saved at ${savedAt}`
      : STORAGE_TEXT[state][language]
  return (
    <div className="ef-opslag" data-stand={state}>
      {/* Een sleutel op de tekst: een nieuwe stand verschijnt opnieuw en vloeit in (besluit van 4 november 2026).
          De vertraging tegen flikkeren zit in `useStorage` (valkuil 7) en verandert daardoor niet. */}
      <span key={text} className="ef-opslag__tekst" aria-hidden="true">
        {text}
      </span>
      <span className="ef-sr-only" role="status" aria-live="polite">
        {isAnnounced(state) ? text : ''}
      </span>
    </div>
  )
}
